<template>
    <DAvatar
        :image="loadedImageUrl"
        :type="type"
        :size="size"
        :icon="avatar?.icon ? `ph:${avatar.icon}` : null"
        v-bind="$attrs"
        @image-error="clearImage"
    >
        {{ avatar?.text }}
    </DAvatar>
</template>

<script setup>
    import { DAvatar } from '@digistorm/spark'
    import { useProfileImagesStore } from '@/stores/profileImages'

    const props = defineProps({
        avatar: Object,
        sizeOverride: String,
    })

    const MAX_ATTEMPTS = 3
    const profileImagesStore = useProfileImagesStore()

    const loadedImageUrl = ref(null)
    const reattemptedUrls = ref([])

    const type = computed(() => {
        if (loadedImageUrl.value) {
            return 'image'
        }
        if (props.avatar?.icon) {
            return 'icon'
        }
        if (props.avatar?.text) {
            return 'initials'
        }
        return 'placeholder'
    })

    const loadImageUrl = () => {
        // Always reset the loaded image URL when loading a new one
        loadedImageUrl.value = null

        if (!props.avatar?.image_url) {
            return
        }

        profileImagesStore.resolvePotentialApiImageUrl(props.avatar.image_url)
            .then((url) => {
                loadedImageUrl.value = url
            })
            .catch((error) => {
                console.log('Unable to load image', error)
            })
    }

    const size = computed(() => {
        switch (props.sizeOverride ?? props.avatar?.size) {
            case 'small':
                return '8'
            case 'large':
                return '16'
            case 'medium-small':
                return '10'
            case 'medium':
            default:
                return '12'
        }
    })

    const clearImage = () => {
        if (!props.avatar?.image_url || !loadedImageUrl.value) {
            return
        }

        // Prevent infinite loops by checking if the URL has already been attempted or if the max attempts have been reached
        if (reattemptedUrls.value.includes(loadedImageUrl.value) || reattemptedUrls.value.length >= MAX_ATTEMPTS) {
            return
        }

        reattemptedUrls.value.push(loadedImageUrl.value)
        profileImagesStore.clearCache(props.avatar.image_url)
        loadImageUrl()
    }

    onMounted(() => {
        loadImageUrl()
    })

    watch(() => props.avatar?.image_url, () => {
        loadImageUrl()
    })
</script>
